/**
 * usePaymentLinkPreview — résout les surcharges visuelles & comportementales
 * de la page /pay/[slug] en cumulant 3 sources, par ordre décroissant de
 * priorité quand `isPreviewMode` est actif :
 *
 *   1. postMessage live depuis l'iframe parent (dashboard) — `preview-update`
 *   2. Query params `previewXxx` (snapshot initial passé à l'iframe)
 *   3. Données SSR `ssrPaymentPage` (valeurs persistées en base)
 *
 * Hors mode preview, seule la source SSR est lue. Le composable factorise
 * également :
 *   - Le parsing des query params (decode URL, "" = explicitement vide,
 *     undefined = absent, etc.)
 *   - L'écoute de `postMessage` côté client + le signal `preview-ready`
 *   - Le scroll-into-view sur les éléments `[data-preview-id]`
 *
 * Cf. /pay/[slug].vue — l'iframe preview du dashboard pousse des
 * `preview-update` au fil des modifications dans le modal d'édition.
 */
import { ref, computed, type ComputedRef } from "vue";
import { useRoute } from "vue-router";
import { getFileUrl } from "~/services/assets";
import type { ImpactOption } from "~/components/donate/DonateImpactOptions.vue";
import { nettoyerMontantsImpact } from "~/utils/montantsImpact";


export interface PreviewCustomField {
  type: "fullname" | "email" | "message";
  required: boolean;
  label: string;
}

/** Surface minimale du `ssrPaymentPage` consommée par le composable. */
export interface SSRPaymentPageSlice {
  banner?: string | null;
  amounts?: number[];
  defaultAmountIndex?: number;
  title?: string | null;
  subtitle?: string | null;
  goalEndDate?: string | null;
  primaryColor?: string | null;
  backgroundColor?: string | null;
  buttonStyle?: string | null;
  submitText?: string | null;
  youtubeUrl?: string | null;
  subscriptionsEnabled?: boolean;
  customFields?: PreviewCustomField[] | null;
  showFeesOption?: boolean;
  showFiscalInfo?: boolean;
  showGoalProgress?: boolean;
  impactOptions?: ImpactOption[] | null;
}

export interface UsePaymentLinkPreviewOptions {
  /** Mode preview iframe — désactive tous les overrides quand `false`. */
  isPreviewMode: ComputedRef<boolean>;
  /** Données SSR de la `paymentPage` (peut être undefined si slug inexistant). */
  ssrPaymentPage: SSRPaymentPageSlice | null | undefined;
  /**
   * Verdict SSR « reçu fiscal annoncé », déjà résolu par `recuFiscalAnnonce`
   * (fermé par défaut). PAS lu dans `ssrPaymentPage` : il vaut null quand
   * l'asso n'a pas de page de paiement, et le verdict se perdait avec.
   */
  ssrShowFiscalInfo: boolean;
}

/** Surcharges live envoyées par l'iframe parent via `postMessage`. */
interface PreviewOverrides {
  primaryColor?: string;
  bgColor?: string;
  buttonStyle?: string;
  submitText?: string;
  youtubeUrl?: string;
  subscriptionsEnabled?: boolean;
  showFeesOption?: boolean;
  showFiscalInfo?: boolean;
  customFields?: PreviewCustomField[] | null;
  impactOptions?: ImpactOption[] | null;
  scrollTo?: string;
  type?: string;
}

/** Origine du dashboard en production — fallback en dur quand la config est vide. */
const PROD_DASHBOARD_ORIGIN = "https://dashboard.donoor.org";

/**
 * SÉCURITÉ — Résout la liste des origines autorisées à piloter le preview.
 *
 * Seul le dashboard qui édite la page (celui qui embarque cette iframe) doit
 * pouvoir : (a) émettre des messages `preview-update` que l'on traite, et
 * (b) recevoir notre signal `preview-ready`. Sans ce filtre, n'importe quelle
 * page tierce embarquant l'iframe pourrait injecter des overrides visuels
 * (spoofing du preview) ou intercepter le postMessage `preview-ready`.
 *
 * - En prod, `config.public.dashboardUrl` est volontairement vide (URLs
 *   relatives via rewrite Vercel) : on retombe sur l'origine prod en dur.
 * - En local/dev, `dashboardUrl` vaut ex. "http://localhost:3000" : on
 *   autorise cette origine en plus, pour que le preview live fonctionne.
 *
 * On ne renvoie que des *origines* (protocole + host + port), jamais un chemin,
 * car `event.origin` et le `targetOrigin` de postMessage sont des origines.
 *
 * La 1re entrée est l'origine « primaire » (celle qu'on cible pour envoyer
 * `preview-ready`) : le `dashboardUrl` configuré passe en tête quand il est
 * présent (dev), sinon on retombe sur l'origine prod en dur.
 */
function resolveAllowedDashboardOrigins(dashboardUrl: string): string[] {
  const origins: string[] = [];
  const trimmed = dashboardUrl.trim();
  if (trimmed) {
    try {
      origins.push(new URL(trimmed).origin);
    } catch {
      // dashboardUrl mal formé → on garde uniquement le fallback prod.
    }
  }
  if (!origins.includes(PROD_DASHBOARD_ORIGIN)) {
    origins.push(PROD_DASHBOARD_ORIGIN);
  }
  return origins;
}

export function usePaymentLinkPreview(opts: UsePaymentLinkPreviewOptions) {
  const route = useRoute();
  const config = useRuntimeConfig();
  const { isPreviewMode, ssrPaymentPage } = opts;

  // SÉCURITÉ — origines de confiance pour le canal postMessage du preview.
  const allowedOrigins = resolveAllowedDashboardOrigins(
    (config.public.dashboardUrl as string) || "",
  );

  // ── Parsing des query params preview ────────────────────────────────────
  /** undefined si absent, null si "", sinon string décodée. */
  function readString(key: string): string | null | undefined {
    const v = route.query[key] as string | undefined;
    if (v === undefined) return undefined;
    if (v === "") return null;
    try {
      return decodeURIComponent(v);
    } catch {
      return v;
    }
  }
  /** undefined si absent, null si "", sinon parseFloat (null si NaN). */
  function readNumber(key: string): number | null | undefined {
    const v = route.query[key] as string | undefined;
    if (v === undefined) return undefined;
    if (v === "") return null;
    const num = parseFloat(v);
    return isNaN(num) ? null : num;
  }

  const previewBanner = computed(
    () => (route.query.previewBanner as string | undefined) || null,
  );
  const previewPrimaryColor = computed(
    () => route.query.previewPrimaryColor as string | undefined,
  );
  const previewBgColor = computed(
    () => route.query.previewBgColor as string | undefined,
  );
  const previewButtonStyle = computed(
    () => route.query.previewButtonStyle as string | undefined,
  );
  const previewSubmitText = computed(() => readString("previewSubmitText") ?? undefined);
  const previewYoutubeUrl = computed(() => readString("previewYoutubeUrl") ?? undefined);
  const previewSubscriptionsEnabled = computed(() => {
    const v = route.query.previewSubscriptionsEnabled as string | undefined;
    return v === undefined ? undefined : v !== "false";
  });
  const previewShowFeesOption = computed(() => {
    const v = route.query.previewShowFeesOption as string | undefined;
    return v === undefined ? undefined : v !== "false";
  });
  const previewCustomFields = computed(() => {
    const v = route.query.previewCustomFields as string | undefined;
    if (!v) return undefined;
    try {
      return JSON.parse(v) as PreviewCustomField[];
    } catch {
      return undefined;
    }
  });
  const previewTitle = computed(() => readString("previewTitle"));
  const previewSubtitle = computed(() => {
    const v = route.query.previewSubtitle as string | undefined;
    if (v === undefined) return undefined;
    if (v === "") return ""; // "" = sous-titre explicitement vide
    try {
      return decodeURIComponent(v);
    } catch {
      return v;
    }
  });
  const previewOrgName = computed(() => readString("previewOrgName") ?? undefined);
  const previewGoal = computed(() => readNumber("previewGoal"));
  const previewEndDate = computed(() => {
    const v = route.query.previewEndDate as string | undefined;
    if (v === undefined) return undefined;
    if (v === "") return null;
    return v;
  });
  const previewShowGoalProgress = computed(() => {
    const v = route.query.previewShowGoalProgress as string | undefined;
    return v === undefined ? undefined : v === "true";
  });
  const previewRaised = computed(() => {
    const v = route.query.previewRaised as string | undefined;
    if (v === undefined) return undefined;
    const num = parseFloat(v);
    return isNaN(num) ? 0 : num;
  });
  // Montants d'impact passés par l'aperçu (JSON [{ amount, label }]).
  const previewImpactOptions = computed<ImpactOption[] | undefined>(() => {
    const raw = route.query.previewImpactOptions as string | undefined;
    if (!raw) return undefined;
    try {
      return nettoyerMontantsImpact(JSON.parse(raw));
    } catch {
      return undefined;
    }
  });
  const previewAmounts = computed(() => {
    const v = route.query.previewAmounts as string | undefined;
    if (!v) return null;
    return v
      .split(",")
      .map((s) => parseFloat(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);
  });
  const previewDefaultAmountIndex = computed(() => {
    const v = route.query.previewDefaultAmountIndex as string | undefined;
    if (!v) return 0;
    const num = parseInt(v, 10);
    return isNaN(num) ? 0 : num;
  });

  // ── Listener postMessage (iframe live updates) ──────────────────────────
  const postMessageOverrides = ref<PreviewOverrides>({});

  if (import.meta.client) {
    window.addEventListener("message", (event) => {
      // SÉCURITÉ — n'accepter les `preview-update` que du dashboard de confiance.
      // Sans ce garde, toute page tierce embarquant l'iframe pourrait injecter
      // des overrides visuels (spoofing du contenu affiché au donateur).
      if (!allowedOrigins.includes(event.origin)) return;
      if (event.data?.type !== "preview-update") return;
      postMessageOverrides.value = { ...event.data };
      if (event.data.scrollTo) {
        nextTick(() => {
          const el = document.querySelector(
            `[data-preview-id="${event.data.scrollTo}"]`,
          );
          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      }
    });
    // Signaler au parent que l'iframe est prête à recevoir les updates.
    // SÉCURITÉ — cibler explicitement l'origine du dashboard (jamais "*") pour
    // ne pas divulguer ce signal à un parent malveillant. On vise l'origine
    // dashboard résolue (dashboardUrl en dev, prod en dur en fallback) : c'est
    // la seule origine légitime censée embarquer et écouter cette iframe.
    // `allowedOrigins` est toujours non vide (PROD_DASHBOARD_ORIGIN y est
    // systématiquement ajouté) ; le `?? PROD_DASHBOARD_ORIGIN` sert uniquement
    // à narrower le type `string | undefined` (noUncheckedIndexedAccess).
    if (isPreviewMode.value) {
      const primaryOrigin = allowedOrigins[0] ?? PROD_DASHBOARD_ORIGIN;
      window.parent.postMessage({ type: "preview-ready" }, primaryOrigin);
    }
  }

  // ── Résolveurs preview > postMessage > query > SSR ──────────────────────
  /** Renvoie la première valeur définie selon la chaîne de priorité. */
  function pickPreview<T>(
    pm: T | undefined,
    queryVal: T | undefined,
    ssrVal: T | null | undefined,
  ): T | null {
    if (isPreviewMode.value && pm !== undefined) return pm;
    if (isPreviewMode.value && queryVal !== undefined) return queryVal as T;
    return (ssrVal as T | null) ?? null;
  }
  /** Idem mais pour booléens dont le défaut métier est `true`. */
  function pickBoolDefaultTrue(
    pm: boolean | undefined,
    q: boolean | undefined,
    ssr: boolean | undefined,
  ): boolean {
    if (isPreviewMode.value && pm !== undefined) return pm;
    if (isPreviewMode.value && q !== undefined) return q;
    return ssr !== false;
  }

  const primaryColor = computed(() =>
    pickPreview(
      postMessageOverrides.value.primaryColor,
      previewPrimaryColor.value,
      ssrPaymentPage?.primaryColor,
    ),
  );
  const backgroundColor = computed(() =>
    pickPreview(
      postMessageOverrides.value.bgColor,
      previewBgColor.value,
      ssrPaymentPage?.backgroundColor,
    ),
  );
  const buttonStyle = computed(() =>
    pickPreview(
      postMessageOverrides.value.buttonStyle,
      previewButtonStyle.value,
      ssrPaymentPage?.buttonStyle,
    ),
  );
  const submitText = computed(() =>
    pickPreview(
      postMessageOverrides.value.submitText,
      previewSubmitText.value,
      ssrPaymentPage?.submitText,
    ),
  );
  const youtubeUrl = computed(() =>
    pickPreview(
      postMessageOverrides.value.youtubeUrl,
      previewYoutubeUrl.value,
      ssrPaymentPage?.youtubeUrl,
    ),
  );

  // Titre & sous-titre : sémantique spéciale ("" en preview = explicitement vide).
  const customTitle = computed(() => {
    if (isPreviewMode.value && previewTitle.value !== undefined) {
      return previewTitle.value;
    }
    return ssrPaymentPage?.title || null;
  });
  const customSubtitle = computed(() => {
    if (isPreviewMode.value && previewSubtitle.value !== undefined) {
      return previewSubtitle.value === "" ? null : previewSubtitle.value;
    }
    return ssrPaymentPage?.subtitle || null;
  });

  // Bannière : preview > SSR. URL résolue via getFileUrl si chemin relatif.
  const bannerUrl = computed(() => {
    let raw: string | null | undefined;
    if (isPreviewMode.value && route.query.previewBanner !== undefined) {
      raw = previewBanner.value;
    } else {
      raw = ssrPaymentPage?.banner;
    }
    if (!raw) return null;
    if (raw.startsWith("http")) return raw;
    // Bannière hero pleine largeur : resize 1920 + webp (sinon image brute de
    // l'asso servie en pleine résolution). (Audit perf 2026-07-31.)
    return getFileUrl(raw, { width: 1920, format: "webp", quality: 80 });
  });

  // Flags booléens : default true sauf si explicitement désactivé.
  const subscriptionsEnabled = computed(() =>
    pickBoolDefaultTrue(
      postMessageOverrides.value.subscriptionsEnabled,
      previewSubscriptionsEnabled.value,
      ssrPaymentPage?.subscriptionsEnabled,
    ),
  );
  const showFeesOption = computed(() =>
    pickBoolDefaultTrue(
      postMessageOverrides.value.showFeesOption,
      previewShowFeesOption.value,
      ssrPaymentPage?.showFeesOption,
    ),
  );
  // ⚠️ Surtout PAS `pickBoolDefaultTrue` : sans verdict, on n'annonce pas de
  // reçu fiscal (cf. recuFiscalAnnonce). En aperçu, le dashboard envoie un
  // interrupteur qui tient déjà compte de l'habilitation de l'asso.
  const showFiscalInfo = computed(() => {
    const pm = postMessageOverrides.value.showFiscalInfo;
    if (isPreviewMode.value && pm !== undefined) return pm;
    return opts.ssrShowFiscalInfo;
  });

  // Champs personnalisés — null = pas configuré = tout activé par défaut.
  const customFields = computed<PreviewCustomField[] | null>(() => {
    if (
      isPreviewMode.value &&
      postMessageOverrides.value.customFields !== undefined
    ) {
      return postMessageOverrides.value.customFields;
    }
    if (isPreviewMode.value && previewCustomFields.value !== undefined) {
      return previewCustomFields.value;
    }
    return ssrPaymentPage?.customFields || null;
  });

  const impactOptions = computed<ImpactOption[]>(() => {
    if (isPreviewMode.value && postMessageOverrides.value.impactOptions !== undefined) {
      return nettoyerMontantsImpact(postMessageOverrides.value.impactOptions);
    }
    if (isPreviewMode.value && previewImpactOptions.value !== undefined) {
      return previewImpactOptions.value;
    }
    return nettoyerMontantsImpact(ssrPaymentPage?.impactOptions);
  });

  return {
    impactOptions,
    // Couleurs & customisation visuelle
    primaryColor,
    backgroundColor,
    buttonStyle,
    submitText,
    youtubeUrl,
    customTitle,
    customSubtitle,
    bannerUrl,

    // Flags comportementaux
    subscriptionsEnabled,
    showFeesOption,
    showFiscalInfo,

    // Champs perso + montants
    customFields,
    previewOrgName,
    previewGoal,
    previewEndDate,
    previewShowGoalProgress,
    previewRaised,
    previewAmounts,
    previewDefaultAmountIndex,
  };
}
